import React, { useState, useEffect } from 'react'
import { observer } from 'mobx-react'
import * as Sentry from '@sentry/browser'
import { logger } from 'spartacus/services/LoggerService'
import useStores from 'spartacus/hooks/useStores'
import Layout from 'spartacus/components/Layout'
import SEO from 'spartacus/components/SEO'
import VerifyEmailStep from 'spartacus/components/VerifyEmailStep'
import PasswordStep from 'spartacus/components/PasswordStep'
import * as S from './style'

enum Step {
  CreatePassword,
  VerifyEmail,
}
export interface Fields {
  password: string
  passwordConfirmation: string
}

export enum InitialValues {
  Password,
  PasswordConfirmation,
}

const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again'

const CreatePasswordView = observer(
  (): JSX.Element => {
    const { sessionStore } = useStores()
    const [currentStep, setCurrentStep] = useState<Step>(Step.CreatePassword)
    const [loading, setLoading] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<string>('')
    const [fields, setFields] = useState<Fields>({
      password: '',
      passwordConfirmation: '',
    })

    useEffect(() => {
      if (!sessionStore.signUpSessionID) {
        // TODO: need to figure out how to handle expired/invalid sessionID
        logger.debug('[user auth 2.0] legacy user, creating sign up session to create password')
        sessionStore.createSignUpSession('password')
      }
    }, [sessionStore.signUpSessionID])

    const handlePasswordFieldsChange = (updatedFields: Fields): void => {
      setFields({
        password: updatedFields.password,
        passwordConfirmation: updatedFields.passwordConfirmation,
      })
    }

    const handleSubmit = async (): Promise<void> => {
      try {
        setLoading(true)

        await sessionStore.updateSignUpSession({
          authentication: {
            password: fields.password,
          },
        })

        logger.debug('[user auth 2.0] legacy user, submitting password success')

        setCurrentStep(Step.VerifyEmail)
        setLoading(false)
      } catch (e) {
        setLoading(false)
        setErrorMessage(typeof e === 'string' ? e : GENERIC_ERROR_MESSAGE)
        logger.debug(`[user auth 2.0] legacy user, submitting password error: ${e}`)
        Sentry.captureException(e)
      }
    }

    return (
      <Layout>
        <SEO title="Create Password" url="/create-password" />
        <S.Container>
          {currentStep === Step.CreatePassword ? (
            <PasswordStep
              loading={loading}
              subtitle="No more difficult 6-digit codes. We heard your feedback and have made it easier to access your account."
              errorMessage={errorMessage}
              initialValues={{
                password: fields.password,
                passwordConfirmation: fields.passwordConfirmation,
              }}
              onChange={handlePasswordFieldsChange}
              onNext={handleSubmit}
            />
          ) : (
            <VerifyEmailStep />
          )}
        </S.Container>
      </Layout>
    )
  },
)

export default CreatePasswordView
